import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactSelect, {
  ReactSelectProps,
  Async as AsyncReactSelect,
  Creatable as CreatableReactSelect,
  ReactCreatableSelectProps,
  NewOptionCreatorHandler,
  OnNewOptionClickHandler,
} from 'react-select';
import { useTranslation } from 'react-i18next';

import { combineClassNames } from '@utils/combineClassNames';
import Icon from '@common/components/icon';
import i18n from '../../../../../i18n';
import { Message } from '../../message';

type Option = Record<string, any>;

export type SelectOptionComponentProps<O extends {} = Option> = {
  children: React.ComponentType;
  option: O;
  isSelected: boolean;
  isFocused?: boolean;
  isDisabled: boolean;
  className?: string;
  onSelect: (option: O) => void;
  onFocus?: (option: O, event: React.MouseEvent | React.FocusEvent) => void;
};

type SimplifiedValueComponentProps = {
  id: string;
  children: Array<string>;
};

type TValue = {
  label?: string | null | undefined;
  value?: string | boolean | number | Record<string, any> | null;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  href?: string;
  target?: string;
  [key: string]: any;
};

type ChipClassNameValueProps = {
  children: React.ReactNode;
  value: TValue;
  onRemove?: (value: TValue) => void;
  disabled?: boolean;
  id?: string;
  onClick?: (value: TValue, event: React.MouseEvent) => void;
};

function createChipClassNameValueComponent({
  getValueChipClassName,
  multiValueClearPosition,
}: {
  getValueChipClassName?: (value: TValue) => string | undefined;
  multiValueClearPosition: 'leading' | 'trailing';
}) {
  return function ChipClassNameValueComponent({
    children,
    value,
    onRemove,
    disabled,
    id,
    onClick,
  }: ChipClassNameValueProps) {
    const chipClassName = getValueChipClassName?.(value);
    const clearAfterLabel = multiValueClearPosition === 'trailing';

    function handleRemove(event: React.SyntheticEvent) {
      event.preventDefault();
      event.stopPropagation();
      onRemove?.(value);
    }

    function handleMouseDown(event: React.MouseEvent) {
      if (event.type === 'mousedown' && event.button !== 0) return;
      if (onClick) {
        event.stopPropagation();
        onClick(value, event);
      } else if (value.href) {
        event.stopPropagation();
      }
    }

    const removeIcon =
      !disabled && onRemove ? (
        <span
          className="Select-value-icon"
          aria-hidden="true"
          onMouseDown={handleRemove}
          onTouchEnd={handleRemove}
        >
          ×
        </span>
      ) : null;

    const label =
      onClick || value.href ? (
        <a
          className="Select-value-label"
          href={value.href}
          rel={value.target === '_blank' ? 'noopener noreferrer' : undefined}
          target={value.target}
          onMouseDown={handleMouseDown}
          onTouchEnd={(event) => handleMouseDown(event as unknown as React.MouseEvent)}
        >
          {children}
        </a>
      ) : (
        <span
          className="Select-value-label"
          role="option"
          aria-selected="true"
          id={id}
        >
          {children}
        </span>
      );

    return (
      <div
        className={combineClassNames('Select-value', chipClassName, value.className)}
        style={value.style}
        title={value.title}
      >
        {clearAfterLabel ? (
          <>
            {label}
            {removeIcon}
          </>
        ) : (
          <>
            {removeIcon}
            {label}
          </>
        )}
      </div>
    );
  };
}

const createSimplifiedValueComponent =
  (values: TValue[]) => ({ id, children }: SimplifiedValueComponentProps) => {
    const segmentedId = id.split('-');
    const index = parseInt(segmentedId[segmentedId.length - 1], 10);
    if (index > 0) return null;
    return (
      <>
        {children}
        {values.length > 1 && ` + ${values.length - 1}`}
      </>
    );
  };

export type SelectProps = Omit<
  ReactSelectProps,
  'value' | 'options' | 'onChange'
> &
  ReactCreatableSelectProps & {
    value?: TValue | string | boolean | number | null;
    onChange: (newValue: any) => void;
    options?: TValue[];
    getOptions?: (value: string) => Promise<TValue[]>;
    OptionComponent?: React.ComponentType<SelectOptionComponentProps>;
    className?: string;
    warning?: boolean | string;
    error?: boolean | string;
    simplifyMultiValue?: boolean;
    loadingPlaceholder?: React.ReactNode;
    autoload?: boolean;
    cache?: boolean;
    newOptionCreator?: NewOptionCreatorHandler | JSX.Element;
    onNewOptionClick?: OnNewOptionClickHandler<TValue>;
    /** Optional chip class per selected value (multi only). When set, uses custom chip renderer; clear defaults to `trailing` unless `multiValueClearPosition` is set. */
    getValueChipClassName?: (value: TValue) => string | undefined;
    /**
     * Multi only, opt-in. When set (or when `getValueChipClassName` is passed), uses custom chips.
     * `trailing` — label then × (like current react-select docs). `leading` — × then label (react-select v1 default).
     */
    multiValueClearPosition?: 'leading' | 'trailing';
    /**
     * Float the menu over scroll containers (task sidebar dropdowns). Off by default —
     * time/timezone selects must keep inline menus or their layout breaks.
     */
    menuEscape?: boolean;
  };

function getFixedContainingBlock(element: Element): HTMLElement | null {
  let ancestor = element.parentElement;
  while (ancestor && ancestor !== document.documentElement) {
    const style = window.getComputedStyle(ancestor);
    const willChange = style.willChange.split(',').map((token) => token.trim());
    if (
      style.transform !== 'none'
      || style.filter !== 'none'
      || style.perspective !== 'none'
      || willChange.includes('transform')
    ) {
      return ancestor;
    }
    ancestor = ancestor.parentElement;
  }

  return null;
}

/** Keep the menu over the field instead of growing a parent scrollbar (react-select v1). */
function useEscapedSelectMenu() {
  const hostRef = useRef<HTMLDivElement>(null);
  const [menuContainerStyle, setMenuContainerStyle] = useState<React.CSSProperties>();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const placeMenu = useCallback(() => {
    const control = hostRef.current?.querySelector('.Select-control');
    if (!(control instanceof HTMLElement)) return;

    const rect = control.getBoundingClientRect();
    const containingBlock = getFixedContainingBlock(control);
    const origin = containingBlock?.getBoundingClientRect();

    setMenuContainerStyle({
      position: 'fixed',
      top: origin ? rect.bottom - origin.top : rect.bottom,
      left: origin ? rect.left - origin.left : rect.left,
      width: rect.width,
      zIndex: 20,
    });
  }, []);

  const stopWatching = useCallback(() => {
    window.removeEventListener('scroll', placeMenu, true);
    window.removeEventListener('resize', placeMenu);
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
  }, [placeMenu]);

  useEffect(() => stopWatching, [stopWatching]);

  const onOpen = useCallback(() => {
    placeMenu();
    window.addEventListener('scroll', placeMenu, true);
    window.addEventListener('resize', placeMenu);

    const control = hostRef.current?.querySelector('.Select-control');
    // Multi chips change the control height after the menu opens. A fixed menu
    // stays at the old bottom unless we follow that resize.
    if (control && typeof ResizeObserver !== 'undefined') {
      resizeObserverRef.current?.disconnect();
      const observer = new ResizeObserver(() => placeMenu());
      observer.observe(control);
      resizeObserverRef.current = observer;
    }
  }, [placeMenu]);

  const onClose = useCallback(() => {
    stopWatching();
  }, [stopWatching]);

  return { hostRef, menuContainerStyle, onOpen, onClose };
}

const createSelect =
  (SelectInputComponent: any, extraProps?: Partial<SelectProps>) => ({
    className,
    getOptions,
    OptionComponent,
    simplifyMultiValue,
    placeholder,
    searchable = false,
    filterOption,
    error,
    warning,
    newOptionCreator,
    onNewOptionClick,
    getValueChipClassName,
    multiValueClearPosition,
    onOpen: onOpenFromProps,
    onClose: onCloseFromProps,
    menuContainerStyle: menuContainerStyleFromProps,
    menuEscape = false,
    ...props
  }: SelectProps) => {
    const { t } = useTranslation();
    const escapedMenu = useEscapedSelectMenu();

    const effectiveMultiClearPosition =
      multiValueClearPosition ??
      (getValueChipClassName !== undefined ? 'trailing' : undefined);

    const useCustomMultiValue =
      !!props.multi &&
      !props.valueComponent &&
      !(simplifyMultiValue && Array.isArray(props.value)) &&
      effectiveMultiClearPosition !== undefined;

    const fullClassName = combineClassNames(className, {
      ReactSelect__MultiSimplified: simplifyMultiValue,
      ReactSelect__MultiClearTrailing:
        useCustomMultiValue && effectiveMultiClearPosition === 'trailing',
      ReactSelect__MultiClearLeading:
        useCustomMultiValue && effectiveMultiClearPosition === 'leading',
      'Form__control--invalid': !warning && !!error,
      'Form__control--warning': !!warning,
    });

    return (
      <div ref={menuEscape ? escapedMenu.hostRef : undefined}>
        <SelectInputComponent
          className={fullClassName}
          loadOptions={
            getOptions &&
            (async (value: string) => {
              const result = await getOptions(value);
              return { options: result };
            })
          }
          // Fixes react-select filtering options which have been returned by the getOptions
          filterOption={getOptions ? () => true : filterOption}
          closeOnSelect={!props.multi}
          searchable={searchable}
          optionComponent={OptionComponent}
          onNewOptionClick={onNewOptionClick}
          {...{ ...props, ...extraProps }}
          menuContainerStyle={menuEscape ? {
            ...escapedMenu.menuContainerStyle,
            ...menuContainerStyleFromProps,
          } : menuContainerStyleFromProps}
          onOpen={menuEscape ? () => {
            escapedMenu.onOpen();
            onOpenFromProps?.();
          } : onOpenFromProps}
          onClose={menuEscape ? () => {
            escapedMenu.onClose();
            onCloseFromProps?.();
          } : onCloseFromProps}
          valueComponent={
            simplifyMultiValue && Array.isArray(props.value)
              ? createSimplifiedValueComponent(props.value as TValue[])
              : props.valueComponent ??
                (useCustomMultiValue
                  ? createChipClassNameValueComponent({
                      getValueChipClassName,
                      multiValueClearPosition: effectiveMultiClearPosition,
                    })
                  : undefined)
          }
          noResultsText={t('common:select_no_results')}
          placeholder={
            <>
              {searchable && <Icon type="search" />}
              {placeholder !== undefined
                ? placeholder
                : (searchable && t('common:select_placeholder_search')) ||
                  t('common:select_placeholder_select')}
            </>
          }
        />
        <Message error={error} warning={warning} />
      </div>
    );
  };

export const Select = createSelect(ReactSelect);
const Async = createSelect(AsyncReactSelect, {
  searchable: true,
  autoload: false,
  cache: false,
  loadingPlaceholder: i18n.t('common:select_loading_placeholder'),
});

const Creatable = createSelect(CreatableReactSelect, {
  searchable: true,
  autoload: false,
  cache: false,
  loadingPlaceholder: i18n.t('common:select_loading_placeholder'),
});

export const AsyncSelect = Async;
export const CreatableSelect = Creatable;
export default Select;
